/**
 * ProgressBar / Progress Component
 *
 * Barra de progresso baseada em @radix-ui/react-progress (padrao shadcn/ui).
 * Suporta variantes de cor, tamanhos, label e modo indeterminate.
 *
 * @module components/ui
 * @since 2026-02-03
 * @updated 2026-04-06 — migrado para shadcn/Radix Progress
 * @issue TMF9-841 TH-50394
 */

import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  /** Valor atual (0-max, default max=100) */
  value?: number;
  /** Valor maximo (padrao: 100) */
  max?: number;
  /** Tamanho da barra */
  size?: 'sm' | 'md' | 'lg';
  /** Variante de cor */
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'auto';
  /** Mostrar label com porcentagem */
  showLabel?: boolean;
  /** Label customizado */
  label?: string;
  /** Modo indeterminate (animacao continua) */
  indeterminate?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAutoColor(percentage: number): string {
  if (percentage >= 90) return 'bg-red-500 dark:bg-red-500';
  if (percentage >= 75) return 'bg-yellow-500 dark:bg-yellow-500';
  if (percentage >= 50) return 'bg-blue-500 dark:bg-blue-500';
  return 'bg-green-500 dark:bg-green-500';
}

const variantIndicator: Record<string, string> = {
  default: 'bg-primary',
  success: 'bg-green-500 dark:bg-green-400',
  warning: 'bg-yellow-500 dark:bg-yellow-400',
  danger: 'bg-red-500 dark:bg-red-400',
};

const sizeClasses: Record<string, string> = {
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-4',
};

// ---------------------------------------------------------------------------
// Progress (shadcn standard export)
// ---------------------------------------------------------------------------

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(
  (
    {
      className,
      value = 0,
      max = 100,
      size = 'md',
      variant = 'default',
      showLabel = false,
      label,
      indeterminate = false,
      ...props
    },
    ref
  ) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
    const indicatorColor =
      variant === 'auto' ? getAutoColor(percentage) : variantIndicator[variant];

    return (
      <div className={cn('w-full', className)}>
        {(showLabel || label) && (
          <div className="flex justify-between items-center mb-1">
            {label && (
              <span className="text-xs text-muted-foreground">{label}</span>
            )}
            {showLabel && (
              <span className="text-xs font-medium text-foreground">
                {Math.round(percentage)}%
              </span>
            )}
          </div>
        )}
        <ProgressPrimitive.Root
          ref={ref}
          value={indeterminate ? undefined : percentage}
          max={100}
          className={cn(
            'relative w-full overflow-hidden rounded-full bg-muted',
            sizeClasses[size]
          )}
          {...props}
        >
          <ProgressPrimitive.Indicator
            className={cn(
              'h-full w-full flex-1 transition-all',
              indicatorColor,
              indeterminate && 'animate-[progress-indeterminate_1.5s_ease-in-out_infinite]'
            )}
            style={
              indeterminate
                ? undefined
                : { transform: `translateX(-${100 - percentage}%)` }
            }
          />
        </ProgressPrimitive.Root>
      </div>
    );
  }
);

Progress.displayName = 'Progress';

// ---------------------------------------------------------------------------
// ProgressBar — alias para compatibilidade retroativa
// ---------------------------------------------------------------------------

export interface ProgressBarProps {
  /** Valor atual */
  value: number;
  /** Valor maximo (padrao: 100) */
  max?: number;
  /** Tamanho da barra */
  size?: 'sm' | 'md' | 'lg';
  /** Variante de cor */
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'auto';
  /** Mostrar label com porcentagem */
  showLabel?: boolean;
  /** Label customizado */
  label?: string;
  /** Classes CSS adicionais */
  className?: string;
  /** Modo indeterminate */
  indeterminate?: boolean;
}

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, max = 100, size = 'md', variant = 'default', showLabel = false, label, className, indeterminate = false }, ref) => (
    <Progress
      ref={ref}
      value={value}
      max={max}
      size={size}
      variant={variant}
      showLabel={showLabel}
      label={label}
      className={className}
      indeterminate={indeterminate}
    />
  )
);

ProgressBar.displayName = 'ProgressBar';

// ---------------------------------------------------------------------------
// UtilizationBar
// ---------------------------------------------------------------------------

export interface UtilizationBarProps {
  /** Valor utilizado */
  used: number;
  /** Valor total */
  total: number;
  /** Tamanho da barra */
  size?: 'sm' | 'md' | 'lg';
  /** Mostrar label com valores */
  showLabel?: boolean;
  /** Funcao para formatar valores */
  formatValue?: (value: number) => string;
  /** Classes CSS adicionais */
  className?: string;
}

export function UtilizationBar({
  used,
  total,
  size = 'md',
  showLabel = true,
  formatValue,
  className = '',
}: UtilizationBarProps) {
  const percentage = total > 0 ? (used / total) * 100 : 0;
  const format = formatValue ?? ((v: number) => v.toLocaleString('pt-BR'));

  return (
    <div className={className}>
      {showLabel && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-muted-foreground">
            {format(used)} / {format(total)}
          </span>
          <span className="text-xs font-medium text-foreground">
            {Math.round(percentage)}%
          </span>
        </div>
      )}
      <ProgressBar value={used} max={total} size={size} variant="auto" />
    </div>
  );
}

export { Progress };
